import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import type { CancellationWindow } from "@/lib/types";

/**
 * The free-cancellation clock.
 *
 * US DOT 14 CFR 259.5(b)(4) gives the traveller 24 hours to cancel for a full
 * refund. It is the one window in which our price-watching is still COSTLESS to
 * act on, so while it is open we say so, and we say how long is left.
 *
 * ── What this component must never do ───────────────────────────────────────
 *
 * It renders only when the backend says `open`. The backend sets that flag only
 * when every condition of the rule is visibly met — booked direct with the
 * carrier, 7+ days before departure, itinerary touching the US, booking time
 * known (see backend `services/cancellationWindow.ts`). This component adds no
 * judgement of its own: it does not infer a window, does not show a "probably",
 * and does not render a reason for a window that is shut. A countdown the
 * traveller cannot rely on is worse than no countdown, because they act on it
 * either way.
 */

/** Recompute from the absolute deadline, so a tab left open stays truthful. */
function minutesLeft(closesAt: string, now: number): number {
  const ms = new Date(closesAt).getTime() - now;
  return ms > 0 ? Math.floor(ms / 60_000) : 0;
}

function label(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

export default function CancellationCountdown({ cancellation }: { cancellation: CancellationWindow }) {
  const closesAt = cancellation.closesAt;
  const [now, setNow] = useState(() => Date.now());

  // Tick every 30s. Cheap, and keeps the minute figure honest without a refetch.
  useEffect(() => {
    if (!cancellation.open || !closesAt) return;
    const id = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(id);
  }, [cancellation.open, closesAt]);

  if (!cancellation.open || !closesAt) return null;

  const mins = minutesLeft(closesAt, now);
  // The deadline passed while the page sat open. Say nothing rather than "0m".
  if (mins <= 0) return null;

  // Under three hours this stops being reassurance and starts being a deadline.
  const urgent = mins < 180;
  const tone = urgent
    ? "border-amber-200 bg-amber-50 text-amber-900"
    : "border-sky-200 bg-sky-50 text-sky-900";
  const iconTone = urgent ? "text-amber-700" : "text-sky-700";
  const bodyTone = urgent ? "text-amber-800" : "text-sky-800";

  return (
    <div className={`mt-2 rounded-lg border px-3 py-2.5 ${tone}`}>
      <div className="flex items-start gap-2">
        <ShieldCheck className={`mt-0.5 h-4 w-4 shrink-0 ${iconTone}`} aria-hidden="true" />
        <div className="min-w-0 flex-1 text-sm">
          <div className="font-medium">
            {label(mins)} left to cancel this for free
          </div>
          <div className={`mt-0.5 text-xs ${bodyTone}`}>
            US rules let you cancel a ticket booked direct with the airline within 24
            hours of buying it, for a full refund. We keep checking the fare in the
            meantime{urgent ? " — so if a cheaper one lands, it still costs you nothing to move." : "."}
          </div>
        </div>
      </div>
    </div>
  );
}
